import Layout from '@/components/layout/Layout';
import { FC } from 'react';
import { useRef, useEffect, useState } from 'react';
import BookBreadcrumbs from '@/components/ui/book-breadcrumbs/BookBreadcrumbs';
import BookInfo from '@/components/ui/book-info/BookInfo';
import BookTabs from '@/components/ui/book-tabs/BookTabs';
import LitresWidget from '@/components/ui/litres-widget/LitresWidget';
import SimilarBooks from '@/components/ui/similar-books/SimilarBooks';
import Pagination from '@/components/ui/pagination/Pagination';
import ReadBanner from '@/components/ui/banners/read-banner/ReadBanner';
import BookMidBtn from '@/components/ui/book-mid-btn/BookMidBtn';
import BookSeries from '@/components/ui/book-series/BookSeries';
import { getAuthorFullName } from '@/helpers/helpers';
import { useGetUrlPath } from '@/hooks/useGetUrlPath';
import { IBookShortDetail } from '@/interfaces/bookShortDetail.interface';
import { IReviews } from '@/interfaces/reviews.interface';
import { IRead } from '@/interfaces/read.interface';
import { IQuotes } from '@/interfaces/quotes.interface';
import { BOOK_WIDGET_READ_ONLINE_LFROM, BTN_READ_ONLINE_LFROM } from '@/helpers/variables';
import styles from './ReadDetail.module.scss';
import ReadJsonMarkup from './read-json-markup/ReadJsonMarkup';

interface ReviewsDetailPage {
  book: IBookShortDetail;
  read: IRead;
  reviews: IReviews;
  quotes: IQuotes;
  pageNumber?: string;
}

const ReadDetail: FC<ReviewsDetailPage> = ({ book, read, reviews, quotes, pageNumber }) => {
  const [isClient, setIsClient] = useState(false);

  const bookData = book.book;

  const otherAuthorBooks = book.other_author_books;
  const bookFullAlias = `/avtor/${book.author.alias}/${book.alias}/`;

  const { first_name, middle_name, last_name } = book.author;

  const authorFullName = getAuthorFullName(first_name, middle_name, last_name);
  const pagesCount = read.pages;

  const pageUrl = decodeURI(useGetUrlPath());

  const refReadContent = useRef<HTMLDivElement>(null);
  const refScrollContainer = useRef<HTMLDivElement>(null);

  const SEO_TITLE = `${bookData.title} - читать онлайн бесплатно без регистрации, ${authorFullName}`;
  const SEO_DESCRIPTION = `${bookData.title} ⏩ читать онлайн бесплатно без регистрации, ✨ ${authorFullName}. 📚 Электронная библиотека КучаКниг.`;
  const SEO_CANONICAL = `${bookFullAlias}read/`;
  const SEO_COVER = `/covers/${bookData.external_id}.jpg`;

  const createMarkup = (c: string) => {
    return { __html: c };
  };

  let renderBannersOnce = true;

  useEffect(() => {
    setIsClient(true);

    if (renderBannersOnce) {
      const paragraphs = refReadContent.current?.querySelectorAll('p');
      const txtImages = refReadContent.current?.querySelectorAll('img');

      if (pageNumber === undefined && txtImages !== undefined && txtImages.length > 0) {
        txtImages[0].style.display = 'none';
      }

      if (paragraphs !== undefined && paragraphs?.length > 0) {
        const middleParagraph = paragraphs[Math.ceil(paragraphs.length / 2)];

        const renderMiddleBanner = (idName: string, className: string) => {
          const yandexBanner = document.createElement('div');
          yandexBanner.setAttribute('id', idName);
          yandexBanner.setAttribute('data', className);

          middleParagraph.after(yandexBanner);
        };
        renderMiddleBanner('yandex_rtb_R-A-745625-6', 'read-banner-desktop');
        renderMiddleBanner('yandex_rtb_R-A-745625-7', 'read-banner-mobile');
      }
    }
    if (!!refScrollContainer) {
      refScrollContainer.current?.scrollIntoView();
    }
    renderBannersOnce = false;
  });

  return (
    <Layout title={SEO_TITLE} description={SEO_DESCRIPTION} pageHref={pageUrl} canonicalHref={SEO_CANONICAL} cover={SEO_COVER} robotsNoFollow>
      {!!book.book && <ReadJsonMarkup book={book} authorName={authorFullName} />}
      <BookBreadcrumbs bookTitle={book.book.title} bookAlias={book.alias} authorFullName={authorFullName} authorAlias={book.author.alias} genres={book.genres} createdAt={book.created_at ?? '2023-12-09T15:50:25.063033'} />
      <BookInfo book={book} readPage />

      {book.hide_litres_data !== true && <LitresWidget bookTitle={bookData.title} lfrom={Number(BOOK_WIDGET_READ_ONLINE_LFROM)} authorName={authorFullName} />}

      {book.series && book.series[0]?.other_series_books.length > 0 && <BookSeries series={book.series} />}

      <div ref={refScrollContainer}>
        <BookTabs bookAlias={bookFullAlias} readPage reviews={!!reviews.data} reviewsCount={reviews.total_count} quotes={quotes.data.length > 0} quotesCount={quotes.total_count} readTxt={true} />
      </div>

      <Pagination defaultCurrentPage={read.page} pagesCount={pagesCount ?? 0} postName={`avtor/${book.author.alias}/${book.alias}/read/`} />

      {!!read && isClient && <div ref={refReadContent} className={styles.readOnline} dangerouslySetInnerHTML={createMarkup(read.content)}></div>}

      <ReadBanner />

      {Number(pageNumber) === pagesCount && book.book?.allow_full_free === 0 && (
        <div className={styles.readBtn}>
          <div className={styles.subTitle}>Конец ознакомительного фрагмента</div>
          <br />
          {book.hide_litres_data !== true && <BookMidBtn txt="Купить полную версию" href={`https://www.litres.ru/pages/biblio_book/?art=${book.book.external_id}&lfrom=${BTN_READ_ONLINE_LFROM}`} target="_blank" rel="nofollow" />}
        </div>
      )}
      <Pagination defaultCurrentPage={read.page} pagesCount={pagesCount ?? 0} postName={`avtor/${book.author.alias}/${book.alias}/read/`} />
      {otherAuthorBooks.length > 0 && <SimilarBooks books={otherAuthorBooks} />}
    </Layout>
  );
};

export default ReadDetail;
